import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDdMmYy } from "@/lib/formatMarket";

export function IntelStrip({ symbol }) {
  const [shadow, setShadow] = useState(null);
  const [talk, setTalk] = useState(null);
  const [ss, setSs] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    const t = encodeURIComponent(symbol);
    api.get(`/shadow-money/${t}`).then(({ data }) => setShadow(data)).catch(() => setShadow(null));
    api.get(`/ticker-talk/${t}`).then(({ data }) => setTalk(data)).catch(() => setTalk(null));
    api.get(`/intel/sentisense-pilot/${t}`).then(({ data }) => setSs(data)).catch(() => setSs(null));
  }, [symbol]);

  const items = Array.isArray(talk?.items) ? talk.items : [];
  const h1 = items[0];
  const h2 = items[1];
  const buys = shadow?.insider?.buy_count_30d;
  const sells = shadow?.insider?.sell_count_30d;
  const congressRows = Array.isArray(shadow?.congress) ? shadow.congress : [];
  const ptrBuys = shadow?.congress?.purchases ?? shadow?.congress?.buy_count
    ?? (congressRows.length ? congressRows.filter((c) => /purchase|buy/i.test(String(c.type || ""))).length : null);
  const ptrSells = shadow?.congress?.sales ?? shadow?.congress?.sell_count
    ?? (congressRows.length ? congressRows.filter((c) => /sale|sell/i.test(String(c.type || ""))).length : null);

  const newsLine = (row) => {
    if (!row) return "—";
    const d = formatDdMmYy(row.date || row.publication_time || row.published_at);
    const head = row.headline || row.article_headline || "—";
    return d ? `[${d}] ${head}` : head;
  };

  return (
    <div className="border rounded-sm px-2 py-1.5 w-full min-w-0"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      data-testid="intel-strip"
      title="Filings may be delayed. Congressional amounts are ranges.">
      <div className="text-[9px] font-bold tracking-widest mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>INTELLIGENCE</div>
      <div className="text-[10px] leading-snug truncate" style={{ color: "var(--hoip-text-secondary)" }} data-testid="intel-news-1">
        {newsLine(h1)}
      </div>
      <div className="text-[10px] leading-snug truncate" style={{ color: "var(--hoip-text-secondary)" }} data-testid="intel-news-2">
        {newsLine(h2)}
      </div>
      <div className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-secondary)" }} data-testid="intel-insiders">
        Insiders (30D): {buys ?? "—"} buys / {sells ?? "—"} sells
      </div>
      <div className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}
        title="Congressional periodic transaction reports. Amounts are disclosed as ranges and can be delayed."
        data-testid="intel-congress">
        Congress (90D): {ptrBuys ?? "—"} purchases / {ptrSells ?? "—"} sales
      </div>
      {ss?.status === "ready" && (ss.direction || ss.score_label) ? (
        <div className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}
          title="Cached SentiSense intelligence. Not live, not execution pricing, not a trade recommendation."
          data-testid="intel-sentisense">
          SentiSense cache: {ss.direction || ss.score_label}
          {ss.as_of ? ` · as of ${String(ss.as_of).slice(0, 10)}` : ""}
        </div>
      ) : null}
      <div className="flex gap-2 mt-0.5 text-[10px]">
        <Link to="/shadow-money" className="text-blue-400 underline">Open Shadow Money</Link>
        <Link to="/ticker-talk" className="text-blue-400 underline">Open Ticker Talk</Link>
      </div>
    </div>
  );
}
